import React from 'react'

import {
  Button,
  ButtonClean,
  List,
  type ListProps,
  Subsection,
  Tooltip,
  useCopyToClipboard,
} from '@fourthwall/components'
import { DuplicateIcon, TickIcon } from '@fourthwall/icons'
import { routing } from '@utils/routing'

import * as S from './Webhooks.styled'
import type { WebhooksListItem } from './Webhooks.types'
import { WebhookListItem } from './components/WebhookListItem/WebhookListItem'

export type WebhooksViewProps = {
  webhooksListProps: Omit<ListProps, 'renderItem'>
  clientSecret?: string
  onWebhookCreate?: () => void
}

export const WebhooksView = ({
  webhooksListProps,
  clientSecret,
  onWebhookCreate,
}: WebhooksViewProps) => {
  const { isCopied, copyToClipboard } = useCopyToClipboard()

  return (
    <Subsection title="Webhooks" id="webhooks">
      <S.Description>
        You can subscribe to events for your products and orders by creating webhooks that will push
        JSON notifications to a given URL.{' '}
        <ButtonClean label="Learn more" href={routing.external.docs.self} target="_blank" />
      </S.Description>

      <S.ButtonContainer>
        <Button
          label="Create webhook"
          size="small"
          disabled={!onWebhookCreate}
          onClick={onWebhookCreate}
        />
      </S.ButtonContainer>

      <List
        renderItem={(webhookItem: WebhooksListItem) => <WebhookListItem {...webhookItem} />}
        {...webhooksListProps}
      />

      {clientSecret && (
        <S.ClientSecretContainer>
          Webhooks will be signed with secret token so you can verify their integrity:
          <br />
          <S.ClientSecretContainerValue>
            {clientSecret}
            {isCopied ? (
              <Tooltip reference={<TickIcon height={12} />}>Copied!</Tooltip>
            ) : (
              <Tooltip
                reference={
                  <ButtonClean
                    label=""
                    icon={DuplicateIcon}
                    onClick={() => copyToClipboard(clientSecret)}
                  />
                }
              >
                Copy
              </Tooltip>
            )}
          </S.ClientSecretContainerValue>
        </S.ClientSecretContainer>
      )}
    </Subsection>
  )
}
